import { useState, useEffect, useRef } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Stethoscope, UserRound, HeartPulse, Send, Loader2, Clock, CheckCircle2, XCircle, LogOut } from "lucide-react";

// Shown after Google login: applies any role picked on the login screen, lets users
// choose Doctor or Doctor's Assistant, and (for patients) renders the patient app.
// Assistants request access to a doctor; once approved they get live shared access.
export default function RoleGate({ children, patientView }) {
  const { user, refresh, logout } = useAuth();
  const [busy, setBusy] = useState(false);
  const [assistantStep, setAssistantStep] = useState("choose"); // choose | request | pending
  const [doctorEmail, setDoctorEmail] = useState("");
  const [requestInfo, setRequestInfo] = useState(null);

  const role = user?.role;
  const approved = user?.assistant_status === "approved";

  // poll for approval while pending
  useEffect(() => {
    if (role !== "assistant" || approved) return;
    const t = setInterval(async () => {
      await refresh();
    }, 5000);
    return () => clearInterval(t);
  }, [role, approved, refresh]);

  // initialize synchronously so the loader renders on the very first frame and the
  // dashboard never mounts before a login-screen role choice is applied
  const [autoApplying, setAutoApplying] = useState(() => {
    try {
      const pending = localStorage.getItem("aura_pending_role");
      return !!(pending && pending !== (user?.role ?? null));
    } catch { return false; }
  });
  const appliedRef = useRef(false);

  // role chosen on the login screen before Google sign-in -> apply it
  // (new account: /role/select; existing account with a different stored role: /role/change)
  useEffect(() => {
    const pending = localStorage.getItem("aura_pending_role");
    if (!pending || appliedRef.current) return;
    if (role != null && pending === role) { localStorage.removeItem("aura_pending_role"); return; }
    appliedRef.current = true;
    setAutoApplying(true);
    (async () => {
      try {
        if (role == null) {
          await api.post("/role/select", { role: pending });
        } else {
          await api.post("/role/change", { role: pending });
        }
        if (pending === "assistant") setAssistantStep("request");
        await refresh();
        toast.success(pending === "doctor" ? "Welcome, Doctor" : pending === "patient" ? "Welcome — book your first online consultation" : "Continuing as Doctor's Assistant");
      } catch { toast.error("Could not set role"); }
      finally {
        localStorage.removeItem("aura_pending_role");
        setAutoApplying(false);
      }
    })();
  }, [role, refresh]);

  const pickDoctor = async () => {
    setBusy(true);
    try {
      await api.post("/role/select", { role: "doctor" });
      await refresh();
      toast.success("Welcome, Doctor");
    } catch { toast.error("Could not set role"); } finally { setBusy(false); }
  };

  const pickAssistant = async () => {
    setBusy(true);
    try {
      await api.post("/role/select", { role: "assistant" });
      await refresh();
      setAssistantStep("request");
    } catch { toast.error("Could not set role"); } finally { setBusy(false); }
  };

  const sendRequest = async () => {
    if (!doctorEmail.trim()) { toast.error("Enter the doctor's email"); return; }
    setBusy(true);
    try {
      const r = await api.post("/access/request", { doctor_email: doctorEmail.trim() });
      setRequestInfo(r.data);
      await refresh();
      setAssistantStep("pending");
      toast.success("Access request sent");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Request failed");
    } finally { setBusy(false); }
  };

  const switchToDoctor = async () => {
    setBusy(true);
    try {
      await api.post("/role/change", { role: "doctor" });
      await refresh();
      toast.success("Welcome, Doctor");
    } catch { toast.error("Could not switch role"); } finally { setBusy(false); }
  };

  // applying the role picked on the login screen (or switching an existing account) -> loader
  if (autoApplying) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FBFBF7] p-6">
        <div data-testid="role-auto-apply" className="rounded-3xl bg-white border border-slate-100 shadow-xl p-8 text-center w-full max-w-lg">
          <Loader2 className="w-8 h-8 text-[#1E5AA8] animate-spin mx-auto" />
          <p className="text-sm text-slate-500 mt-3">Setting up your account…</p>
        </div>
      </div>
    );
  }

  // Doctor or approved assistant -> app
  if (role === "doctor" || (role === "assistant" && approved)) return children;

  // Patient -> patient app (after any pending login-screen role switch has applied above)
  if (role === "patient") return patientView || null;

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FBFBF7] p-6">
      <div className="w-full max-w-lg">
        <div className="flex items-center gap-3 justify-center mb-8">
          <div className="w-11 h-11 rounded-2xl bg-[#1E5AA8] flex items-center justify-center shadow-md"><HeartPulse className="w-6 h-6 text-amber-300" /></div>
          <h1 className="font-display text-2xl font-bold text-[#123C73]">MDvia Care</h1>
        </div>

        {role == null && (
          <div data-testid="role-select" className="rounded-3xl bg-white border border-slate-100 shadow-xl p-8">
            <h2 className="font-display text-2xl font-bold text-slate-900 text-center">How will you use MDvia Care?</h2>
            <p className="text-sm text-slate-500 text-center mt-1">Choose your role to continue.</p>
            <div className="grid sm:grid-cols-2 gap-4 mt-6">
              <button data-testid="role-doctor" onClick={pickDoctor} disabled={busy}
                className="rounded-2xl border-2 border-slate-200 hover:border-[#1E5AA8] p-6 text-left transition-colors group">
                <div className="w-12 h-12 rounded-xl bg-[#EBF3FB] flex items-center justify-center mb-3 group-hover:bg-[#1E5AA8] transition-colors"><Stethoscope className="w-6 h-6 text-[#1E5AA8] group-hover:text-white" /></div>
                <h3 className="font-semibold text-slate-900">I'm a Doctor</h3>
                <p className="text-xs text-slate-500 mt-1">Full access — consultations, AI prescriptions, certificates, patients, inventory & more.</p>
              </button>
              <button data-testid="role-assistant" onClick={pickAssistant} disabled={busy}
                className="rounded-2xl border-2 border-slate-200 hover:border-amber-400 p-6 text-left transition-colors group">
                <div className="w-12 h-12 rounded-xl bg-amber-50 flex items-center justify-center mb-3 group-hover:bg-amber-400 transition-colors"><UserRound className="w-6 h-6 text-amber-600 group-hover:text-white" /></div>
                <h3 className="font-semibold text-slate-900">I'm a Doctor's Assistant</h3>
                <p className="text-xs text-slate-500 mt-1">Book & manage appointments for your doctor. Requires the doctor's approval.</p>
              </button>
            </div>
          </div>
        )}

        {role === "assistant" && !approved && (
          <div data-testid="assistant-access" className="rounded-3xl bg-white border border-slate-100 shadow-xl p-8">
            {user?.assistant_status !== "pending" && assistantStep !== "pending" && (
              <>
                <h2 className="font-display text-2xl font-bold text-slate-900">Request Access</h2>
                <p className="text-sm text-slate-500 mt-1">Enter your doctor's account email. They'll approve your access to manage their appointments.</p>
                <div className="mt-5">
                  <label className="text-xs font-medium text-slate-500 mb-1 block">Doctor's Email</label>
                  <input data-testid="input-doctor-email" type="email" value={doctorEmail} onChange={(e) => setDoctorEmail(e.target.value)}
                    placeholder="doctor@clinic.com" className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E5AA8]/30" />
                </div>
                <button data-testid="btn-send-access-request" onClick={sendRequest} disabled={busy}
                  className="mt-4 w-full inline-flex items-center justify-center gap-2 py-3 rounded-xl bg-[#1E5AA8] text-white font-semibold hover:bg-[#123C73] disabled:opacity-60">
                  {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-4 h-4" />} Send Access Request
                </button>
              </>
            )}
            {(assistantStep === "pending" || user?.assistant_status === "pending") && (
              <div className="text-center py-4">
                <div className="w-16 h-16 rounded-2xl bg-amber-50 flex items-center justify-center mx-auto"><Clock className="w-8 h-8 text-amber-500 animate-pulse" /></div>
                <h2 className="font-display text-2xl font-bold text-slate-900 mt-4">Waiting for Approval</h2>
                <p className="text-sm text-slate-500 mt-2">Your request to {requestInfo?.doctor_name || "the doctor"} is pending. This page will update automatically once approved — no need to log in again.</p>
                {user?.assistant_status === "rejected" && (
                  <div className="mt-4 rounded-xl bg-red-50 border border-red-200 p-3 text-sm text-red-700 inline-flex items-center gap-2"><XCircle className="w-4 h-4" /> Your previous request was declined.</div>
                )}
                <div className="mt-5 flex justify-center gap-2">
                  <button onClick={() => setAssistantStep("request")} className="text-sm text-[#1E5AA8] font-medium hover:underline">Request a different doctor</button>
                </div>
              </div>
            )}

            <div className="mt-6 pt-4 border-t border-slate-100 text-center">
              <button data-testid="switch-to-doctor-btn" onClick={switchToDoctor} disabled={busy}
                className="text-xs text-slate-400 hover:text-[#1E5AA8] inline-flex items-center gap-1.5 disabled:opacity-50">
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Stethoscope className="w-3.5 h-3.5" />}
                Picked the wrong role? Switch to a Doctor account
              </button>
            </div>
          </div>
        )}

        <button onClick={logout} className="mt-6 mx-auto flex items-center gap-2 text-sm text-slate-400 hover:text-red-500"><LogOut className="w-4 h-4" /> Sign out</button>
      </div>
    </div>
  );
}
